import { TldrawUiDialogHeader, useEditor } from "tldraw"
import { useCurrentPage } from "../hooks/useCurrentPage"
import {
  Button,
  DialogTitle,
  TextField,
  Typography,
  Box,
  InputAdornment,
  IconButton,
  DialogContent,
  Dialog,
  Radio,
} from "@mui/material"
import { Controller, useForm, useFieldArray } from "react-hook-form"
import ClearIcon from "@mui/icons-material/Close"
import AddIcon from "@mui/icons-material/Add"
import { useState } from "react"
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core"
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable"
import { ExpressionInput } from "../pages/ExpressionInput"
import { Parameter } from "../../shared/Parameter"

type Props = {
  paramId: string
  onClose: () => void
}

export function EditParameter({ paramId, onClose }: Props) {
  const page = useCurrentPage()
  const parameter = page.meta.parameters[paramId]
  const editor = useEditor()
  const [useExpressionOpen, setUseExpressionOpen] = useState(false)

  const { control, handleSubmit, setValue, watch } = useForm({
    defaultValues:
      parameter.type === "number"
        ? {
            min: parameter.min ?? "",
            max: parameter.max ?? "",
            step: parameter.step ?? "",
          }
        : parameter.type === "string"
          ? {
              options: parameter.options ?? [],
              defaultOptionIndex: 0,
            }
          : {},
  })

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: "options",
  })

  const mouseSensor = useSensor(MouseSensor, {
    activationConstraint: { distance: 10 },
  })
  const touchSensor = useSensor(TouchSensor, {
    activationConstraint: { distance: 22 },
  })
  const sensors = useSensors(mouseSensor, touchSensor)

  const defaultOptionIndex = watch("defaultOptionIndex") as number

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return

    const oldIndex = fields.findIndex((f) => f.id === active.id)
    const newIndex = fields.findIndex((f) => f.id === over.id)

    move(oldIndex, newIndex)

    // Adjust defaultOptionIndex accordingly
    if (defaultOptionIndex === oldIndex) {
      setValue("defaultOptionIndex", newIndex)
    } else if (
      defaultOptionIndex > oldIndex &&
      defaultOptionIndex <= newIndex
    ) {
      // Moved an earlier item down past the default
      setValue("defaultOptionIndex", defaultOptionIndex - 1)
    } else if (
      defaultOptionIndex < oldIndex &&
      defaultOptionIndex >= newIndex
    ) {
      // Moved a later item up past the default
      setValue("defaultOptionIndex", defaultOptionIndex + 1)
    }
  }

  const handleNumberSubmit = (data: { min: any; max: any; step: any }) => {
    editor.updatePage({
      id: page.id,
      meta: {
        ...page.meta,
        parameters: {
          ...page.meta.parameters,
          [paramId]: {
            ...parameter,
            min: data.min !== "" ? Number(data.min) : null,
            max: data.max !== "" ? Number(data.max) : null,
            step: data.step !== "" ? Number(data.step) : null,
          },
        },
      },
    })
    onClose()
  }

  const handleStringSubmit = (data: {
    options: string[]
    defaultOptionIndex: number
  }) => {
    editor.updatePage({
      id: page.id,
      meta: {
        ...page.meta,
        parameters: {
          ...page.meta.parameters,
          [paramId]: {
            ...parameter,
            options: data.options,
          },
        },
        parameterValues: {
          ...page.meta.parameterValues,
          [paramId]:
            Expr.lit(data.options[data.defaultOptionIndex]) || Expr.lit(""),
        },
      },
    })
    onClose()
  }

  return (
    <>
      <TldrawUiDialogHeader>
        <DialogTitle>{parameter.name}</DialogTitle>
      </TldrawUiDialogHeader>

      {parameter.type === "number" && (
        <DialogContent>
          <Box
            component="form"
            onSubmit={handleSubmit(handleNumberSubmit)}
            sx={{ flex: 1 }}
          >
            <Typography variant="subtitle1">Parameter Settings</Typography>

            {["min", "max", "step"].map((name) => (
              <Controller
                key={name}
                name={name}
                control={control}
                render={({ field }) => (
                  <TextField
                    label={name[0].toUpperCase() + name.slice(1)}
                    type="number"
                    fullWidth
                    size="small"
                    variant="outlined"
                    value={field.value}
                    onChange={field.onChange}
                    style={{ paddingBottom: 10 }}
                    InputProps={{
                      endAdornment: field.value !== "" && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setValue(name as any, "")}
                            edge="end"
                            size="small"
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
              />
            ))}

            <Box display="flex" justifyContent="flex-end">
              <Button type="submit" variant="contained" color="primary">
                Save
              </Button>
            </Box>
          </Box>
        </DialogContent>
      )}

      {parameter.type === "string" && (
        <DialogContent>
          <Typography variant="subtitle1" gutterBottom>
            Text Options
          </Typography>

          <Box component="form" onSubmit={handleSubmit(handleStringSubmit)}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            >
              <SortableContext
                items={fields.map((field) => field.id)}
                strategy={verticalListSortingStrategy}
              >
                {fields.map((field, index) => (
                  <SortableItem key={field.id} id={field.id}>
                    {({ setNodeRef, listeners, attributes, style }) => (
                      <Box
                        ref={setNodeRef}
                        {...attributes}
                        {...listeners}
                        style={style}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          pb: 1,
                          cursor: "grab",
                          backgroundColor: "#f9f9f9",
                          borderRadius: 1,
                          px: 1,
                          py: 0.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 16,
                            textAlign: "center",
                            userSelect: "none",
                          }}
                        >
                          ☰
                        </Box>
                        <Controller
                          name="defaultOptionIndex"
                          control={control}
                          render={({ field }) => (
                            <Radio
                              checked={field.value === index}
                              onChange={() => field.onChange(index)}
                              value={index}
                              name="defaultOptionIndex"
                              color="primary"
                              size="small"
                            />
                          )}
                        />
                        <Controller
                          name={`options.${index}`}
                          control={control}
                          render={({ field }) => (
                            <TextField
                              {...field}
                              size="small"
                              variant="outlined"
                              fullWidth
                              placeholder={`Option ${index + 1}`}
                            />
                          )}
                        />
                        <IconButton size="small" onClick={() => remove(index)}>
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    )}
                  </SortableItem>
                ))}
              </SortableContext>
            </DndContext>

            <Button
              startIcon={<AddIcon />}
              onClick={() => append("")}
              sx={{ mt: 2 }}
            >
              Add Option
            </Button>

            <Box display="flex" justifyContent="flex-end" mt={2}>
              <Button type="submit" variant="contained" color="primary">
                Save
              </Button>
            </Box>
          </Box>
        </DialogContent>
      )}

      {useExpressionOpen && (
        <Dialog
          open={useExpressionOpen}
          onClose={() => setUseExpressionOpen(false)}
        >
          <ExpressionInput
            env={page.meta.parameterValues}
            defaultExpression={page.meta.parameterValues[paramId]}
            onSubmit={(expr) => {
              setUseExpressionOpen(false)
              editor.updatePage({
                ...page,
                meta: {
                  ...page.meta,
                  parameterValues: {
                    ...page.meta.parameterValues,
                    [paramId]: expr,
                  },
                },
              })
            }}
          />
        </Dialog>
      )}
    </>
  )
}

import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from "@dnd-kit/modifiers"
import { Expr } from "../../shared/Expr"

function SortableItem({
  id,
  children,
}: {
  id: string
  children: (props: {
    setNodeRef: (el: HTMLElement | null) => void
    listeners: any
    attributes: any
    style: React.CSSProperties
  }) => React.ReactNode
}) {
  const {
    setNodeRef,
    listeners,
    attributes,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 999 : "auto",
  }

  return <>{children({ setNodeRef, listeners, attributes, style })}</>
}
